import { useState } from 'react';
import { Laptop, Camera, Fingerprint, Package, Plus, Search, Filter } from 'lucide-react';

const categories = [
  { id: 'all', label: 'All Items', icon: Package },
  { id: 'laptops', label: 'Laptops', icon: Laptop },
  { id: 'cctv', label: 'CCTV Cameras', icon: Camera },
  { id: 'biometrics', label: 'Biometrics', icon: Fingerprint },
];

export const MasterData = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <div data-testid="master-data-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Master Data</h1>
          <p className="text-slate-600 mt-1">Manage your inventory items and categories</p>
        </div>
        <button
          data-testid="add-item-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Add Item
        </button>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {categories.map((cat) => {
          const Icon = cat.icon;
          const isActive = activeCategory === cat.id;
          
          return (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              data-testid={`category-${cat.id}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-sm font-medium transition-colors ${
                isActive
                  ? 'bg-slate-900 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              <Icon className="h-4 w-4" />
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-input"
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none transition-colors"
            />
          </div>
          <button
            data-testid="filter-btn"
            className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors"
          >
            <Filter className="h-4 w-4" />
            Filters
          </button>
        </div>
      </div>

      {/* Data Table / Empty State */}
      <div className="bg-white rounded-sm border border-slate-200">
        {/* Table Header */}
        <div className="hidden md:grid grid-cols-6 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Item</div>
          <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Category</div>
          <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Brand</div>
          <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">SKU</div>
          <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Stock</div>
          <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Actions</div>
        </div>

        {/* Empty State */}
        <div className="text-center py-16" data-testid="empty-state">
          <Package className="h-16 w-16 mx-auto mb-4 text-slate-300" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No items yet</h3>
          <p className="text-slate-500 mb-6">Start by adding your first inventory item</p>
          <button
            data-testid="empty-add-item-btn"
            className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors"
          >
            <Plus className="h-5 w-5" />
            Add First Item
          </button>
        </div>
      </div>
    </div>
  );
};
